/* eslint-disable react/display-name */
import React, { useEffect } from 'react'
import { Col, Row, Space, Divider } from 'antd'
import { formatDateWithoutTime, getBankName, getSupplierPaymentType, numberWithComma } from 'helpers/format'
import styled from 'styled-components'
import { MyTheme } from 'Components/common/Theme'
import { TableListPage } from 'Components/common/Table'
import ConfirmWithChild from 'Components/modal/ConfirmWithChild'
import { GrnData } from 'Store/grn/types'
import { SupplierData } from 'Store/supplier/types'
import { PurchaseData } from 'Store/purchase/types'

type Props = {
  visible: boolean
  onCloseModal: () => void
  onConfirm?: () => void
  grnList?: GrnData[]
  supplier?: SupplierData
}

const ConfirmPayDept: React.FunctionComponent<Props> = props => {
  const { onCloseModal, visible, grnList = [], onConfirm = () => {}, supplier } = props

  useEffect(() => {}, [visible])

  const countTotalPrice = () => {
    let price = 0
    grnList.forEach(item => {
      price += item.total_recieve_price || 0
    })
    return price
  }

  const columnsSummary = [
    // {
    //   title: 'เลขที่ใบสั่งซื้อ',
    //   dataIndex: 'purchasing_no',
    //   key: 'purchasing_no',
    //   width: '180px',
    // },
    {
      title: 'เลขที่ใบสั่งซื้อ',
      dataIndex: 'purchasing',
      key: 'purchasing_no',
      width: '180px',
      render: (val: PurchaseData) => val?.purchasing_no || '',
    },
    {
      title: 'GRN',
      dataIndex: 'grn_no',
      key: 'grn_no',
    },
    {
      title: 'วันที่รับเข้าสินค้า',
      dataIndex: 'recieved_date',
      key: 'recieved_date',
      render: (value: any) => {
        return formatDateWithoutTime(value)
      },
    },
    {
      title: <TextRight>ราคารวม</TextRight>,
      dataIndex: 'total_recieve_price',
      key: 'total_recieve_price',
      render: (value: any) => {
        return <TextRight>{numberWithComma(value, true)}</TextRight>
      },
    },
  ]

  const renderInfoItem = (title: string, value: any) => {
    return (
      <Row>
        <Col lg={10} xs={12}>
          <InfoTitle>{title} </InfoTitle>
        </Col>
        <Col lg={14} xs={12}>
          <InfoValue>{value || '-'}</InfoValue>
        </Col>
      </Row>
    )
  }

  const renderConfirmInfo = () => {
    return (
      <ConfirmWrapper>
        <Row gutter={[8, 8]}>
          <Col span={24}>
            <Center>
              <Desc>กรุณาตรวจสอบความถูกต้องของข้อมูลก่อน &quot;ยืนยันชำระหนี้&quot;</Desc>
            </Center>
          </Col>
          <Col span={24}>
            <InfoValue>ข้อมูลบริษัท/Supplier</InfoValue>
          </Col>
          <Col md={12} xs={24}>
            {renderInfoItem('รหัสบริษัท', supplier?.supplier_code)}
            {renderInfoItem('ชื่อบริษัท/Supplier', supplier?.name)}
            {renderInfoItem('เลขประจำตัวผู้เสียภาษี', supplier?.tax_payer_id)}
            {renderInfoItem('เบอร์โทรศัพท์มือถือ', supplier?.mobile_no)}
            {renderInfoItem('ที่อยู่บริษัท', supplier?.address)}
          </Col>
          <Col md={12} xs={24}>
            {renderInfoItem('ชื่อบัญชี', supplier?.account_name)}
            {renderInfoItem('เลขที่บัญชี', supplier?.account_no)}
            {renderInfoItem('ธนาคาร', getBankName(supplier?.account_bank))}
            {renderInfoItem('สาขา', supplier?.account_branch)}
            {renderInfoItem('รูปแบบการชำระเงิน', getSupplierPaymentType(supplier?.payment_type))}
            {renderInfoItem('ระยะเวลาชำระเงิน', supplier?.payment_period + ' วัน')}
          </Col>
          <Divider />
          <Col span={24}>
            <InfoValue>สรุปรายการจ่ายหนี้</InfoValue>
          </Col>
          <Col span={24}>
            <TableListPage
              columns={columnsSummary}
              datas={grnList}
              isLoading={false}
              paginationContent={null}
              size="small"
              scrollX={500}
            />
          </Col>
          <Col span={24}>
            <Row justify="space-between" align="middle" gutter={[32, 8]}>
              <Col>
                <Space>จำนวนทั้งหมด {numberWithComma(grnList.length)} รายการ</Space>
              </Col>
              <Col>
                <Space>
                  ยอดรวมทั้งหมด <Price>{numberWithComma(countTotalPrice(), true)}</Price> บาท
                </Space>
              </Col>
            </Row>
          </Col>
        </Row>
      </ConfirmWrapper>
    )
  }

  return (
    <ConfirmWithChild
      title={'ยืนยันชำระหนี้'}
      isShow={visible}
      handleOk={onConfirm}
      handleCancel={onCloseModal}
      child={renderConfirmInfo()}
      cancelBtnTxt="ปิด"
      confirmBtnTxt="ยืนยันชำระหนี้"
      width={'60%'}
    />
  )
}

const Center = styled.div`
  text-align: center;
`

const Price = styled.div`
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.primary};
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.title1};
`
const ConfirmWrapper = styled.div`
  padding: 10px;
  text-align: left;
`

const InfoTitle = styled.span`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.grey};
`

const InfoValue = styled.span`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.black};
`

const TextRight = styled.div`
  text-align: right;
  margin-right: 20px;
`

const Desc = styled.span`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.primary};
`

export default ConfirmPayDept
